import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { FileText, Upload, FileSpreadsheet, Cloud } from 'lucide-react';

// ---------------------------------------------------------------------------
// Method availability config — source of truth for which methods are wired up
// ---------------------------------------------------------------------------

export type EntryMethod = 'manual' | 'csv' | 'pdf' | 'xero';
export type MethodAvailability = 'available' | 'coming_soon';

/**
 * Method availability config.
 * - manual: fully wired (spreadsheet interface)
 * - csv: fully wired (template upload / paste)
 * - pdf: AI extraction is refused in code (P4, record 0024) — greyed out until
 *   the server extraction lane is rebuilt and approved
 * - xero: no XERO_CLIENT_ID in production — greyed out until credentials exist
 */
export const METHOD_AVAILABILITY: Record<EntryMethod, MethodAvailability> = {
  manual: 'available',
  csv: 'available',
  pdf: 'coming_soon',
  xero: 'coming_soon',
} as const;

// ---------------------------------------------------------------------------
// Xero OAuth popup infrastructure
// ---------------------------------------------------------------------------

export interface XeroOAuthPopupConfig {
  url: string;
  target: string;
  width: number;
  height: number;
  windowFeatures: string;
}

/**
 * Open (or describe) a Xero OAuth popup window.
 *
 * When `dryRun` is true the function returns the popup config without calling
 * `window.open()` — useful for unit tests running in Node.
 *
 * When called in a browser with `dryRun: false` (default) it will open a
 * centered popup window pointing at the Xero OAuth authorize endpoint.
 *
 * The popup posts credentials back to the opener via `window.postMessage`.
 */
export function openXeroOAuthPopup(
  options: { dryRun?: boolean } = {},
): XeroOAuthPopupConfig {
  const width = 600;
  const height = 700;
  const left = typeof window !== 'undefined'
    ? Math.round((window.screen.width - width) / 2)
    : 100;
  const top = typeof window !== 'undefined'
    ? Math.round((window.screen.height - height) / 2)
    : 100;

  const windowFeatures =
    `popup=yes,width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`;

  const config: XeroOAuthPopupConfig = {
    url: '/api/xero/oauth/authorize',
    target: 'xero-oauth-popup',
    width,
    height,
    windowFeatures,
  };

  if (!options.dryRun && typeof window !== 'undefined') {
    window.open(config.url, config.target, config.windowFeatures);
  }

  return config;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface WizardStepMethodProps {
  entryMethod?: EntryMethod;
  onSelect: (method: EntryMethod) => void;
  onNext: () => void;
  onBack: () => void;
  onCancel: () => void;
}

const METHOD_CARDS: Array<{
  value: EntryMethod;
  label: string;
  description: string;
  icon: React.ElementType;
}> = [
  {
    value: 'manual',
    label: 'Manual Entry',
    description: 'Enter Profit & Loss and Balance Sheet data directly in the spreadsheet interface',
    icon: FileText,
  },
  {
    value: 'csv',
    label: 'CSV/Excel Import',
    description: 'Upload from template or paste from spreadsheet',
    icon: Upload,
  },
  {
    value: 'pdf',
    label: 'PDF Extraction',
    description: 'Upload accountant financials or tax return PDF',
    icon: FileSpreadsheet,
  },
  {
    value: 'xero',
    label: 'Xero Sync',
    description: 'Pull data from connected Xero organisation',
    icon: Cloud,
  },
];

/**
 * Wizard Step 4: Data Entry Method
 *
 * Radio group:
 * - Manual Entry (available)
 * - CSV/Excel Import (available)
 * - PDF Extraction (coming soon)
 * - Xero Sync (coming soon)
 */
export const WizardStepMethod: React.FC<WizardStepMethodProps> = ({
  entryMethod,
  onSelect,
  onNext,
  onBack,
  onCancel,
}) => {
  const isNextDisabled = !entryMethod;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold" data-testid="wizard-step-title">
          Data Entry Method
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Choose how you will provide the financial data
        </p>
      </div>

      <div className="space-y-4">
        <RadioGroup
          value={entryMethod}
          onValueChange={(val) => {
            const method = val as EntryMethod;
            if (METHOD_AVAILABILITY[method] === 'available') {
              onSelect(method);
            }
          }}
        >
          {METHOD_CARDS.map((card) => {
            const availability = METHOD_AVAILABILITY[card.value];
            const isComingSoon = availability === 'coming_soon';
            const Icon = card.icon;

            return (
              <div
                key={card.value}
                className={`flex items-start space-x-3 space-y-0 rounded-md border p-4 ${
                  isComingSoon ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <RadioGroupItem
                  value={card.value}
                  id={`method-${card.value}`}
                  data-testid={`radio-method-${card.value}`}
                  disabled={isComingSoon}
                />
                <div className="flex-1">
                  <Label
                    htmlFor={`method-${card.value}`}
                    className={`text-base font-medium flex items-center gap-2 ${
                      isComingSoon ? 'cursor-not-allowed' : 'cursor-pointer'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{card.label}</span>
                    {isComingSoon ? (
                      <Badge variant="secondary" data-testid={`badge-${card.value}`}>
                        Coming soon
                      </Badge>
                    ) : (
                      <Badge variant="default" className="bg-green-600" data-testid={`badge-${card.value}`}>
                        Available
                      </Badge>
                    )}
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {card.description}
                  </p>
                </div>
              </div>
            );
          })}
        </RadioGroup>
      </div>

      <div className="flex justify-between pt-4">
        <div className="flex gap-2">
          <Button variant="outline" onClick={onBack} data-testid="button-wizard-back">
            Back
          </Button>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
        <Button onClick={onNext} disabled={isNextDisabled} data-testid="button-wizard-next">
          Next
        </Button>
      </div>
    </div>
  );
};
